import type { User, UserRole } from '@/lib/types';

/*
 * Role helpers.
 *
 * The API has five role slugs but only TWO permission tiers today
 * (see the backend's docs/api/roles.md):
 *
 *   - `viewer` reads only.
 *   - `basic_user` and `advanced_user` may create and edit.
 *   - `admin` and `super_admin` may additionally delete, and own users,
 *     reference masters, countries and currencies.
 *
 * Hence `isAdminTier` rather than a bare `role === 'admin'` check: the two
 * admin slugs are interchangeable today, and branching on `admin` alone locks
 * every super-admin out of the write controls. `basic_user` and
 * `advanced_user` are likewise identical, so nothing here distinguishes them —
 * they are separate labels only so the tiers can diverge later, and the UI must
 * not imply a difference that does not exist yet.
 */

/**
 * Display labels. Send the slug, render the label — the API never takes the
 * label.
 *
 * 🔴 **RE-WORDED 2026-09-02, on the owner's report that the Users page was
 * showing the DEPRECATED vocabulary.** *SUPERSEDED: `viewer: 'Viewer'`,
 * `basic_user: 'Basic User'`, `advanced_user: 'Advanced User'`.*
 *
 * Backend **migration 132** (owner's decision, 1 September 2026) renamed the
 * five seeded permission sets, and `f_role_default_sets` maps role to set
 * one-for-one — so those names ARE the business's word for each tier, and the
 * three old labels are the names of sets that no longer exist. Measured live
 * 2026-09-02, `d_permission_sets` by `sort_order`:
 *
 *     viewer         -> Operations
 *     basic_user     -> University Partnerships
 *     advanced_user  -> Accounts
 *     admin          -> Admin
 *     super_admin    -> Super Admin
 *
 * ⚠️ **THE SLUGS DID NOT MOVE AND MUST NOT.** `basic_user` is still what is
 * stored, sent and guarded on; only what a person reads has changed. The
 * migration froze the set slugs for the same reason.
 *
 * ⚠️ **THIS MIRRORS THOSE NAMES; IT DOES NOT READ THEM.** A set is renameable
 * by any super-admin through `/permission-sets`, so the two CAN drift — and
 * when they do, this map is right and the set row is a set. The label belongs
 * to the ROLE, which is a fixed contract; resolving it from an admin-editable
 * row would make a person's tier read differently on two screens the moment
 * somebody edited a name, which is this codebase's free-text-read-as-a-flag
 * trap wearing a label's clothes. If the owner changes the vocabulary again,
 * change it HERE.
 *
 * ⚠️ **A team name over a capability tier is a claim the items do not make** —
 * migration 132 says so at length and keeps the sets' own descriptions as the
 * only thing on screen that still states the capability. `Accounts` does not
 * scope anything to the accounts team: it is full CRM access including delete.
 * Nothing here may imply otherwise, and `denialReason()` must keep explaining a
 * refusal in terms of what the person may DO, never in terms of a team.
 */
export const ROLE_LABELS: Record<UserRole, string> = {
  viewer: 'Operations',
  basic_user: 'University Partnerships',
  advanced_user: 'Accounts',
  // 🔴 'Admin' -> 'Management', 2026-09-04, owner's instruction. THE SLUG DOES
  // NOT MOVE: `admin` is the wire value and a join key, and `f_role_default_sets`
  // provisions new users by it.
  //
  // ⚠️ MIRRORED FROM `d_permission_sets`, NOT READ FROM IT — measured live that
  // day: set 4 (`administrator`) was ALREADY named 'Management'. Somebody renamed
  // it through /permission-sets and this map stayed on the old word, so the app
  // named a set that no longer existed and NOTHING HERE COULD SEE IT: no build
  // breaks, no type moves, the column renders perfectly. Second time this exact
  // drift has been reported by the owner rather than caught by us -- migration
  // 132 was the first.
  admin: 'Management',
  super_admin: 'Super Admin',
};

/**
 * Human label for a role slug.
 *
 * Takes `string` rather than `UserRole` deliberately: the value arrives from
 * the API, so a slug added server-side before this file catches up must render
 * as itself rather than blanking the cell on an index miss.
 */
export function roleLabel(role: string): string {
  return ROLE_LABELS[role as UserRole] ?? role;
}

/*
 * Both predicates enumerate the roles that are ALLOWED rather than excluding
 * the ones that are not. A negative test (`role !== 'viewer'`) fails *open* the
 * day the API adds a role: a second read-only tier would silently be granted
 * write. Listing the permitted set fails closed instead, so an unrecognised
 * slug, `undefined` and `null` all get the least privilege.
 */
const ADMIN_ROLES: readonly string[] = ['admin', 'super_admin'];
const WRITE_ROLES: readonly string[] = ['basic_user', 'advanced_user', 'admin', 'super_admin'];

/**
 * Slug-level admin test. Exists because some callers only have the slug — a
 * role badge renders a `UserRole`, not a `User` — and without it those places
 * re-inline `role === 'admin' || role === 'super_admin'`, which is the exact
 * duplication this module exists to prevent.
 */
export function isAdminRole(role?: string | null): boolean {
  return role != null && ADMIN_ROLES.includes(role);
}

/** True for `admin` and `super_admin` — the tier that may delete and manage users. */
export function isAdminTier(user: User | null | undefined): boolean {
  return isAdminRole(user?.role);
}

/**
 * True for everyone except `viewer`: the tiers allowed to create and edit.
 *
 * **Exported and ready, but not yet applied.** Every page still gates create
 * and edit on the admin tier, which is stricter than the API now allows;
 * loosening that is a deliberate, separately reviewed change.
 */
export function canWrite(user: User | null | undefined): boolean {
  return WRITE_ROLES.includes(user?.role ?? '');
}

/*
 * ⚠️ **PROMOTED HERE FROM `@/lib/support-session` ON 8 SEPTEMBER 2026, ON THAT
 * FILE'S OWN INSTRUCTION.** It carried this predicate privately under a comment
 * reading *"exporting from a shared module for a single consumer changes a file
 * for no gain here. Promote it the day a second caller appears."*
 * `BuildSwitcher` is that second caller, so the local copy is gone and this is
 * the only definition.
 *
 * Written as a list like its two siblings above, though the tier holds exactly
 * one slug today. That is not ceremony: the value of this file is that all
 * three tiers are answered the same way, so a reader who has understood one has
 * understood the others — and a second top-tier slug becomes one entry rather
 * than a rewritten predicate.
 */
const SUPER_ADMIN_ROLES: readonly string[] = ['super_admin'];

/**
 * Slug-level test for the top tier.
 *
 * Enumerates the slug that IS the tier rather than excluding the ones that are
 * not, for the reason both predicates above give: a negative test
 * (`role !== 'admin'`) fails *open* the day the API adds a role.
 *
 * ⚠️ **Never test the bare string against `ADMIN_ROLES`-style logic in reverse.**
 * `super_admin` is a strict superset of `admin`, so an `isAdminRole` check
 * already passes for this tier — `isSuperAdminRole` is NARROWER, and the two
 * are not interchangeable in either direction.
 */
export function isSuperAdminRole(role?: string | null): boolean {
  return role != null && SUPER_ADMIN_ROLES.includes(role);
}

/**
 * True for `super_admin` alone — the tier that may start a support session and
 * see the build switcher.
 *
 * ⚠️ **Both callers are UI affordances, and neither is a security boundary.**
 * The support dialog's own header says it: the control is the optimisation, the
 * server's refusal is the guarantee. The build switcher is weaker still — it
 * hides a *menu*, while the preview builds it lists are served by nginx to
 * anyone holding the URL. Nothing may be gated on this predicate that the
 * server does not independently refuse.
 */
export function isSuperAdmin(user: User | null | undefined): boolean {
  return isSuperAdminRole(user?.role);
}
